import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * CivixThread — 2026 Nonpartisan Campaign Single Page
 * Tech: React (single file), TailwindCSS only (no external UI libs)
 *
 * Notes:
 * - Top-left now shows platform brand only (“CivixThread”)—no “Hosted with”.
 * - Added a Follow button in the top nav (desktop + mobile) with follower count.
 * - Everything is static/demo; wire up actions later.
 */

const brand = {
  primary: "#0b1f1a", // deep green
  accent: "#0e918c", // teal
  sand: "#f6f5f1",
  ink: "#0c1116",
};

function classNames(...c) {
  return c.filter(Boolean).join(" ");
}

/* Replaces the old "Hosted with" treatment — now just the CivixThread brand */
function SupportedBy({ to = "/" }) {
  return (
    <Link to={to} aria-label="CivixThread" className="flex items-center gap-2 select-none no-underline">
      {/* <div className="relative h-8 w-8 rounded-xl bg-gradient-to-br from-emerald-700 to-teal-500 shadow-inner" /> */}
      <span className="text-xl font-extrabold tracking-tight" style={{ color: brand.primary }}>
        Civix<span className="text-teal-600">Thread</span>
      </span>
    </Link>
  );
}

function NavFollowButton({ isFollowing, followers, onToggle, className = "" }) {
  const followersDisplay = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(followers);

  return (
    <button
      type="button"
      aria-pressed={isFollowing}
      onClick={onToggle}
      className={classNames(
        "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition",
        isFollowing
          ? "bg-teal-600 text-white hover:bg-teal-700 shadow"
          : "text-teal-700 hover:bg-teal-50",
        className
      )}
      style={{ borderColor: brand.accent }}
      title={isFollowing ? "Unfollow" : "Follow"}
    >
      {isFollowing ? "Following" : "Follow"}
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700">
        {followersDisplay}
      </span>
    </button>
  );
}

function CTAButtons() {
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3">
      <a
        href="#donate"
        className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-white shadow-md transition hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
        style={{ background: brand.accent }}
      >
        Donate
      </a>
      <a
        href="#volunteer"
        className="inline-flex items-center justify-center rounded-xl border px-5 py-3 text-base font-semibold text-teal-700 transition hover:bg-teal-50"
        style={{ borderColor: brand.accent }}
      >
        Volunteer
      </a>
      <a
        href="#issues"
        className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-teal-800/80 hover:text-teal-900"
      >
        See Issues →
      </a>
    </div>
  );
}

function NonpartisanChip() {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide text-teal-700"
      style={{ borderColor: brand.accent }}
    >
      <span className="inline-block h-2 w-2 rounded-full bg-teal-500" /> Nonpartisan • 2026
    </span>
  );
}

function SampleBanner() {
  return (
    <div className="inline-flex items-center gap-3 rounded-full border-2 border-teal-300 bg-teal-50 px-4 py-2 text-sm sm:text-base text-teal-900 shadow-sm">
      <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-black tracking-wide text-white">
        STATIC EXAMPLE
      </span>
      <span className="hidden md:inline text-teal-900/80">Text, actions & buttons are placeholders</span>
    </div>
  );
}

function Hero() {
  return (
    <section id="home" className="relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="h-full w-full bg-gradient-to-br from-emerald-50 via-white to-teal-50" />
        <div className="pointer-events-none absolute inset-0 opacity-20 [background-image:radial-gradient(ellipse_at_top,_rgba(14,145,140,0.25),_transparent_50%)]" />
        <div className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 h-[48rem] w-[48rem] rounded-full bg-teal-200 blur-3xl opacity-20" />
      </div>

      <div className="mx-auto max-w-7xl px-6 pt-10 pb-20 lg:pb-28">
        <div className="flex items-center justify-between">
          <SampleBanner />
          <NonpartisanChip />
        </div>

        <div className="mt-14 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h1 className="text-4xl font-black tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Taylor Lee for Congress <span className="text-teal-600">CA-12</span>
            </h1>
            <p className="mt-4 max-w-prose text-lg leading-8 text-slate-700">
              A results-driven, nonpartisan voice focused on practical solutions: safer communities, thriving local
              businesses, modern infrastructure, and accountable government.
            </p>
            <CTAButtons />
            <p className="mt-4 text-sm text-slate-500">Paid for by Friends of Taylor Lee.</p>
          </div>

          <div className="relative">
            <div className="relative mx-auto aspect-[4/3] w-full max-w-xl overflow-hidden rounded-3xl border bg-white shadow-xl">
              {/* Portrait placeholder */}
              <div className="absolute inset-0 grid place-items-center bg-[linear-gradient(135deg,#e6f9f7,white)]">
                <div className="h-36 w-36 rounded-full bg-gradient-to-br from-emerald-700 to-teal-500 shadow-2xl" />
                <span className="mt-4 text-sm font-medium text-slate-500">Campaign portrait placeholder</span>
              </div>
            </div>
            <div className="absolute -left-4 -top-4 rounded-2xl border bg-white/80 px-4 py-2 text-sm font-semibold backdrop-blur">
              Community-first
            </div>
            <div className="absolute -right-4 -bottom-4 rounded-2xl border bg-white/80 px-4 py-2 text-sm font-semibold backdrop-blur">
              Data-informed
            </div>
          </div>
        </div>

        {/* Quick stats */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: "Town Halls Hosted", value: "48+" },
            { label: "Local Endorsements", value: "30+" },
            { label: "Volunteers", value: "1,200+" },
            { label: "Small-Dollar Donors", value: "3,500+" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border bg-white p-5 text-center">
              <div className="text-3xl font-black text-slate-900">{s.value}</div>
              <div className="mt-1 text-sm font-medium text-slate-600">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Issues() {
  const issues = [
    {
      title: "Safe, Vibrant Neighborhoods",
      body:
        "Invest in community-based public safety, mental health response, and youth programs that reduce crime and build trust.",
    },
    {
      title: "Local Business & Jobs",
      body: "Cut red tape for small businesses, expand apprenticeship pipelines, and keep talent here with good-paying jobs.",
    },
    {
      title: "Smart Infrastructure",
      body: "Fix roads and bridges, modernize transit, and invest in clean water and broadband—built to last.",
    },
    {
      title: "Accountable Government",
      body: "Transparent budgeting, open data, and practical oversight so tax dollars work for everyone.",
    },
  ];

  return (
    <section id="issues" className="scroll-mt-24 bg-white py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Priorities</h2>
          <p className="mt-3 text-slate-600">
            Nonpartisan doesn’t mean neutral. It means focused on solutions that work—grounded in data and community
            input.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {issues.map((it) => (
            <div key={it.title} className="group rounded-2xl border p-6 transition hover:shadow-md">
              <h3 className="text-lg font-bold text-slate-900 group-hover:text-teal-700">{it.title}</h3>
              <p className="mt-2 text-slate-600">{it.body}</p>
              <div className="mt-4 h-1 w-12 rounded-full bg-teal-600/20 group-hover:bg-teal-600/40" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Endorsements() {
  const logos = ["Neighbor Assoc.", "Small Business Council", "Transit Riders", "Clean Water Co-op"];
  return (
    <section id="endorsements" className="scroll-mt-24 bg-slate-50 py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <h2 className="text-3xl font-black text-slate-900 sm:text-4xl">Local Support</h2>
            <p className="mt-2 max-w-prose text-slate-600">Built with neighbors, not special interests.</p>
          </div>
          <a href="#volunteer" className="text-teal-700 hover:underline">
            Join the coalition →
          </a>
        </div>
        <div className="mt-8 grid items-center gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {logos.map((l) => (
            <div
              key={l}
              className="grid place-items-center rounded-2xl border bg-white p-6 text-center text-sm font-semibold text-slate-600"
            >
              {l}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Events() {
  const events = [
    {
      date: "Oct 12, 2025",
      title: "Neighborhood Listening Session",
      where: "Mission Community Center",
    },
    { date: "Nov 3, 2025", title: "Small Business Roundtable", where: "Chamber of Commerce" },
    { date: "Dec 7, 2025", title: "Transit & Safety Town Hall", where: "Roosevelt High" },
  ];

  return (
    <section id="events" className="scroll-mt-24 bg-white py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Upcoming Events</h2>
          <p className="mt-3 text-slate-600">Come ask questions, share ideas, and help shape the 2026 platform.</p>
        </div>

        {/* Events grid */}
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((e) => (
            <div key={e.title} className="flex items-center justify-between gap-4 rounded-2xl border bg-white p-5">
              <div>
                <div className="text-sm font-semibold text-teal-700">{e.date}</div>
                <div className="text-lg font-bold text-slate-900">{e.title}</div>
                <div className="text-sm text-slate-600">{e.where}</div>
              </div>
              <button
                type="button"
                className="inline-flex items-center rounded-lg border px-3 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50"
                style={{ borderColor: brand.accent }}
              >
                Add to Calendar
              </button>
            </div>
          ))}
        </div>

        {/* New CTA button under the grid */}
        <div className="mt-10 flex justify-center">
          <a
            href="#alerts"
            className="rounded-xl bg-teal-600 px-6 py-3 text-base font-semibold text-white shadow hover:bg-teal-700"
          >
            Sign-up for Alerts on Events
          </a>
        </div>
      </div>
    </section>
  );
}

function Donate() {
  return (
    <section id="donate" className="scroll-mt-24 bg-gradient-to-b from-teal-50 to-white py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Fuel the 2026 movement</h2>
            <p className="mt-3 max-w-prose text-slate-600">
              We’re building a people-powered, nonpartisan campaign. Every contribution helps us host town halls,
              publish transparent plans, and meet voters where they are.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {[10, 25, 50, 100, 250].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className="rounded-xl border px-4 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50"
                  style={{ borderColor: brand.accent }}
                >
                  ${amt}
                </button>
              ))}
              <button
                type="button"
                className="rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-md"
              >
                Custom amount
              </button>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              By donating you certify that this contribution is made from your own funds and is not from a corporation or
              foreign national.
            </p>
          </div>

          <div className="rounded-3xl border bg-white p-6 shadow">
            <h3 className="text-lg font-bold text-slate-900">Make it monthly</h3>
            <p className="mt-1 text-sm text-slate-600">Sustaining donations keep organizing strong through November 2026.</p>
            <form onSubmit={(e) => e.preventDefault()} className="mt-4 grid gap-4 sm:grid-cols-2">
              <input className="w-full rounded-xl border px-4 py-2 text-sm" placeholder="First name" />
              <input className="w-full rounded-xl border px-4 py-2 text-sm" placeholder="Last name" />
              <input className="w-full rounded-xl border px-4 py-2 text-sm sm:col-span-2" placeholder="Email" type="email" />
              <button
                type="submit"
                className="w-full rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-md sm:col-span-2"
              >
                Start monthly support
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- CAMPAIGN MERCH SECTION -------------------- */
function Merch() {
  const products = [
    {
      key: "tote",
      name: "Canvas Tote Bag",
      price: "$18",
      blurb: "Sturdy 12oz cotton • 15×16″ • One-color print",
      badge: "Best Seller",
      bg: "from-emerald-100 to-teal-50",
    },
    {
      key: "sweater",
      name: "Crewneck Sweater",
      price: "$42",
      blurb: "Mid-weight fleece • Unisex fit • Sizes XS-XXL",
      badge: "New",
      bg: "from-teal-100 to-white",
    },
    {
      key: "stickers",
      name: "Sticker Pack (5)",
      price: "$6",
      blurb: "Weatherproof vinyl • Assorted designs",
      badge: "Budget-friendly",
      bg: "from-white to-emerald-50",
    },
  ];

  return (
    <section id="merch" className="scroll-mt-24 bg-slate-50 py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Campaign Merch</h2>
            <p className="mt-2 max-w-prose text-slate-600">
              Outfit supporters and raise funds with easy, on-brand products.
            </p>
          </div>
          <p
            className="inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold text-teal-700"
            style={{ borderColor: brand.accent }}
          >
            Add custom merch
          </p>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <article key={p.key} className="group rounded-2xl border bg-white overflow-hidden hover:shadow-md transition">
              {/* Image placeholder */}
              <div className={classNames("relative h-48 w-full bg-gradient-to-br", p.bg, "grid place-items-center")}>
                {/* simple product silhouette */}
                <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-500 opacity-90 shadow-lg" />
                <span className="absolute left-3 top-3 rounded-full bg-teal-600/90 px-2 py-0.5 text-[10px] font-bold tracking-wide text-white">
                  {p.badge}
                </span>
              </div>

              {/* Body */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
                  <div className="rounded-lg bg-teal-50 px-2 py-1 text-sm font-semibold text-teal-700">{p.price}</div>
                </div>
                <p className="mt-1 text-sm text-slate-600">{p.blurb}</p>

                <div className="mt-4 flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex w-full items-center justify-center rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-teal-700"
                  >
                    Add to cart
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50"
                    style={{ borderColor: brand.accent }}
                  >
                    Details
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Custom merch CTA / “we take care of it” explainer */}
        <div id="custom-merch" className="mt-8 rounded-2xl border bg-white p-6 sm:p-7 lg:p-8">
          <div className="max-w-2xl">
            <h3 className="text-lg font-bold text-slate-900">Want something unique for your campaign?</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              Beyond the standard tote bags, sweaters, and stickers, we can help you set up **custom, hassle-free campaign merch**.
              Our team manages design, production, payments, and fulfillment so you can focus on connecting with voters — not handling orders.
            </p>
            <p className="mt-2 text-sm text-slate-600">You choose the items, we take care of the logistics.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Volunteer() {
  return (
    <section id="volunteer" className="scroll-mt-24 bg-slate-50 py-16">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">Volunteer & get updates</h2>
            <p className="mt-3 max-w-prose text-slate-600">
              Knock doors, phonebank, host a house meeting, or help with data and design. There’s a place for you here.
            </p>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {["Door-to-door canvassing", "Community events", "Social media & design", "Data & research"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm text-slate-700">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-teal-500" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <form onSubmit={(e) => e.preventDefault()} className="rounded-3xl border bg-white p-6 shadow">
            <div className="grid gap-4 sm:grid-cols-2">
              <input className="w-full rounded-xl border px-4 py-2 text-sm" placeholder="Full name" />
              <input className="w-full rounded-xl border px-4 py-2 text-sm" placeholder="Email" type="email" />
              <input className="w-full rounded-xl border px-4 py-2 text-sm sm:col-span-2" placeholder="Phone" />
              <select className="w-full rounded-xl border px-4 py-2 text-sm sm:col-span-2">
                <option>How do you want to help?</option>
                <option>Canvassing</option>
                <option>Phonebank</option>
                <option>Host an event</option>
                <option>Digital / Design</option>
                <option>Data / Research</option>
              </select>
              <button
                type="submit"
                className="w-full rounded-xl bg-teal-600 px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-md sm:col-span-2"
              >
                Count me in
              </button>
              <p className="sm:col-span-2 text-xs text-slate-500">By submitting, you agree to receive campaign updates. Opt out anytime.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 py-10 text-slate-300">
      <div className="mx-auto max-w-7xl px-6">
        {/* Top row */}
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          {/* Right: nav links */}
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {[
              ["Home", "#home"],
              ["Priorities", "#issues"],
              ["Endorsements", "#endorsements"],
              ["Events", "#events"],
              ["Donate", "#donate"],
              ["Merch", "#merch"],
              ["Volunteer", "#volunteer"],
            ].map(([label, href]) => (
              <a key={label} href={href} className="transition-colors text-slate-400 hover:text-white">
                {label}
              </a>
            ))}
          </nav>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 border-t border-white/10 pt-6 text-xs text-slate-400 leading-relaxed">
          Paid for by Friends of Taylor Lee. Use of military or government imagery does not imply endorsement.
        </div>
      </div>

      {/* Floating donate on mobile */}
      <div className="fixed bottom-4 right-4 z-50 sm:hidden">
        <a href="#donate" className="rounded-full bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-lg hover:bg-teal-700">
          Donate
        </a>
      </div>
    </footer>
  );
}

export default function CampaignLanding() {
  const [open, setOpen] = useState(false);

  // Follow state (demo)
  const [followers, setFollowers] = useState(12840); // starting followers
  const [isFollowing, setIsFollowing] = useState(true);
  const toggleFollow = () => {
    setIsFollowing((prev) => {
      const next = !prev;
      setFollowers((c) => c + (next ? 1 : -1));
      return next;
    });
  };

  // Hide the app's global nav via a body attribute while this page is mounted
  useEffect(() => {
    document.body.setAttribute("data-campaign", "true");
    return () => document.body.removeAttribute("data-campaign");
  }, []);

  const followersDisplay = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(followers);

  return (
    <main className="min-h-screen scroll-smooth bg-white text-slate-900">
      {/* Skip to content */}
      <a
        href="#home"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Skip to main content
      </a>

      {/* Top Nav */}
      <header className="sticky top-0 z-40 w-full border-b bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          {/* Platform brand only */}
          <SupportedBy to="/" />

          {/* Desktop nav */}
          <nav className="hidden items-center gap-6 sm:flex">
            <a href="#issues" className="text-sm font-semibold text-slate-700 hover:text-teal-700">
              Priorities
            </a>
            <a href="#endorsements" className="text-sm font-semibold text-slate-700 hover:text-teal-700">
              Endorsements
            </a>
            <a href="#events" className="text-sm font-semibold text-slate-700 hover:text-teal-700">
              Events
            </a>
            <a href="#merch" className="text-sm font-semibold text-slate-700 hover:text-teal-700">
              Merch
            </a>
            <a href="#volunteer" className="text-sm font-semibold text-slate-700 hover:text-teal-700">
              Volunteer
            </a>

            {/* NEW: Follow button with count */}
            <NavFollowButton
              isFollowing={isFollowing}
              followers={followers}
              onToggle={toggleFollow}
            />

            <a
              href="#donate"
              className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow"
              style={{ background: brand.accent }}
            >
              Donate
            </a>

            {/* Fake Cart Icon with badge "1" */}
            <a href="#merch" aria-label="Cart (1)" className="relative inline-flex items-center">
              <svg
                className="h-6 w-6 text-slate-700 hover:text-teal-700"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 12.39a2 2 0 0 0 2 1.61h8.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span className="absolute -right-2 -top-1 grid h-5 w-5 place-items-center rounded-full bg-teal-600 text-[10px] font-bold text-white shadow">
                1
              </span>
            </a>
          </nav>

          {/* Mobile menu button */}
          <button
            className="sm:hidden inline-flex items-center justify-center rounded-lg border px-3 py-2 text-sm"
            onClick={() => setOpen((v) => !v)}
          >
            Menu
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="sm:hidden border-t bg-white">
            <nav className="mx-auto max-w-7xl px-6 py-3 grid gap-3">
              {[
                ["Priorities", "#issues"],
                ["Endorsements", "#endorsements"],
                ["Events", "#events"],
                ["Merch", "#merch"],
                ["Volunteer", "#volunteer"],
                ["Donate", "#donate"],
                ["Cart (1)", "#merch"],
              ].map(([label, href]) => (
                <a key={label} href={href} className="text-sm font-semibold text-slate-700">
                  {label}
                </a>
              ))}

              {/* NEW: Mobile Follow control */}
              <button
                onClick={toggleFollow}
                className={classNames(
                  "mt-1 inline-flex items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold",
                  isFollowing ? "bg-teal-600 text-white" : "text-teal-700 hover:bg-teal-50"
                )}
                style={{ borderColor: brand.accent }}
              >
                {isFollowing ? "Following" : "Follow"} • {followersDisplay}
              </button>
            </nav>
          </div>
        )}
      </header>

      <Hero />
      <Issues />
      <Endorsements />
      <Events />
      <Donate />
      <Merch /> {/* <-- inserted here */}
      <Volunteer />
      <Footer />
    </main>
  );
}
